import React, { useState } from 'react';
import { Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClubInterestForm = ({ compact = false, onSuccess, testid = 'club-interest-form' }) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: '', club: '', role: '', email: '', phone: '', message: '' });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name';
    if (!form.club.trim()) return 'Please enter your club name';
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return 'Please enter a valid email address';
    if (!/^[0-9 +()-]{8,}$/.test(form.phone.trim())) return 'Please enter a valid phone number';
    return null;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/game-on/register-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setDone(true);
        if (onSuccess) onSuccess();
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid={`${testid}-done`}>
        <div className="w-14 h-14 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-7 h-7 text-[#3D5A47]" />
        </div>
        <h3 className="text-xl font-black text-[#3D5A47]">You're on the list! 🎉</h3>
        <p className="text-sm text-gray-600 mt-2 max-w-sm mx-auto">
          Thanks for registering <strong>{form.club}</strong>. Our team will be in touch shortly to help your club get grant-ready.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3.5" data-testid={testid}>
      <div className={compact ? 'space-y-3.5' : 'grid sm:grid-cols-2 gap-3.5'}>
        <div>
          <Label htmlFor={`${testid}-name`} className="text-sm font-bold text-[#3D5A47]">Your Name*</Label>
          <Input id={`${testid}-name`} data-testid={`${testid}-name`} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Smith" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
        </div>
        <div>
          <Label htmlFor={`${testid}-club`} className="text-sm font-bold text-[#3D5A47]">Club Name*</Label>
          <Input id={`${testid}-club`} data-testid={`${testid}-club`} value={form.club} onChange={(e) => set('club', e.target.value)} placeholder="Werribee Districts FC" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
        </div>
        <div>
          <Label htmlFor={`${testid}-role`} className="text-sm font-bold text-[#3D5A47]">Your Role</Label>
          <Input id={`${testid}-role`} data-testid={`${testid}-role`} value={form.role} onChange={(e) => set('role', e.target.value)} placeholder="e.g. President, Secretary" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
        </div>
        <div>
          <Label htmlFor={`${testid}-phone`} className="text-sm font-bold text-[#3D5A47]">Phone*</Label>
          <Input id={`${testid}-phone`} data-testid={`${testid}-phone`} type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="0412 345 678" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
        </div>
        <div className={compact ? '' : 'sm:col-span-2'}>
          <Label htmlFor={`${testid}-email`} className="text-sm font-bold text-[#3D5A47]">Email*</Label>
          <Input id={`${testid}-email`} data-testid={`${testid}-email`} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@club.com" className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400" />
        </div>
      </div>
      {!compact && (
        <div>
          <Label htmlFor={`${testid}-message`} className="text-sm font-bold text-[#3D5A47]">Anything we should know? (optional)</Label>
          <Textarea id={`${testid}-message`} data-testid={`${testid}-message`} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Tell us about your facility or what you're hoping to upgrade..." className="mt-1.5 bg-[#f8faf5] border-gray-200 text-gray-700 placeholder:text-gray-400 min-h-[80px]" />
        </div>
      )}
      <Button type="submit" disabled={loading} className="w-full bg-[#3D5A47] hover:bg-[#2a4032] text-white gap-2 text-base font-semibold py-6 rounded-xl shadow-lg shadow-[#3D5A47]/20" data-testid={`${testid}-submit`}>
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Register My Club's Interest <ArrowRight className="w-4 h-4" /></>}
      </Button>
      <p className="text-[11px] text-gray-400 text-center">No obligation. We'll help you understand your options and get grant-ready.</p>
    </form>
  );
};

export default ClubInterestForm;
